import { selMessage } from "@selector/shared";
import { useCallback, useMemo } from "react";
import type { FieldArrayFieldsProps, InjectedFormProps, WrappedFieldArrayProps } from "redux-form";
import { FieldArray, reduxForm } from "redux-form";
import { CalloutIntent, DialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import { isDefinedNumber } from "@/redux/modules/sample-admin.ts";

import { isConfigurableParentAttribute } from "../../app-utils/sample.js";
import { filterTemplateSuggestions } from "../../app-utils/sample-template.js";
import { trimOrThrow } from "../../app-utils/string.js";
import { type Attribute, type RawAttributeDefault } from "../../graphql.js";
import ParentAttributeConfigurationFields from "../core/redux-form/ParentAttributeConfigurationFields.js";
import { type IAttributeWithDefault } from "./AddSampleTypeDialog.js";

export const CONFIGURE_PARENT_TYPE_ATTRIBUTE_FORM_NAME = "CONFIGURE_PARENT_TYPE_ATTRIBUTE_FORM";
export const CONFIGURE_PARENT_TYPE_ATTRIBUTE_ATTRIBUTES_FIELD_ARRAY = "attributes";
export const CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER = "defaultNumber";
export const CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT = "defaultText";
export const CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM = "defaultVocTerm";
export const CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE = "template";

interface IConfigureParentTypeAttributeDialogOwnProps {
  onApply?: (configuredAttributes: IAttributeWithDefault[]) => void;
  onCancel?: () => void;
  templateAttributes: Attribute[];
  attributes: IAttributeWithDefault[];
  parentTypeName: string;
  usedInOtherSampleTypes?: boolean;
}

interface IFieldValue extends Partial<RawAttributeDefault> {
  attribute: IAttributeWithDefault;
}

interface IInitialValues {
  attributes?: IFieldValue[];
}

export interface IConfigureParentTypeAttributeDialogProps
  extends IConfigureParentTypeAttributeDialogOwnProps,
    InjectedFormProps<IInitialValues, IConfigureParentTypeAttributeDialogOwnProps> {}

const ConfigureParentTypeAttributeDialogImpl = (props: IConfigureParentTypeAttributeDialogProps): JSX.Element => {
  const { overlayProps, state } = useOverlay({
    defaultOpen: true,
    onOpenChange(isOpen) {
      if (!isOpen) {
        props.onCancel?.();
      }
    },
  });

  const attributeDefaultExists = useCallback((attributeDefault?: Partial<RawAttributeDefault>) => {
    return (
      attributeDefault &&
      (attributeDefault.defaultTerm ||
        attributeDefault.defaultText ||
        isDefinedNumber(attributeDefault.defaultNumber) ||
        attributeDefault.template)
    );
  }, []);

  const { attributes, onApply } = props;

  const handleSubmitData = useCallback(
    (values: IInitialValues): Promise<boolean> => {
      const configuredAttributes: IAttributeWithDefault[] = attributes.map((attribute: IAttributeWithDefault) => {
        const configuredValueRow = values.attributes?.find(
          (valueRow: IFieldValue) => valueRow.attribute.id === attribute.id,
        );
        const numberDefault = configuredValueRow?.[CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER];
        const configurationPayload: Partial<RawAttributeDefault> = {
          defaultNumber: numberDefault ? Number(numberDefault) : null,
          defaultText: trimOrThrow(configuredValueRow?.[CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT]),
          defaultTerm: configuredValueRow?.[CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM],
          template: trimOrThrow(configuredValueRow?.[CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE]),
        };

        return {
          ...attribute,
          attributeDefault: attributeDefaultExists(configurationPayload) ? configurationPayload : undefined,
        };
      });

      onApply?.(configuredAttributes);
      return Promise.resolve(true);
    },
    [attributeDefaultExists, attributes, onApply],
  );

  const buildParentAttributeConfigurationFieldsComponent = useCallback(
    (fieldArrayProps: WrappedFieldArrayProps<IFieldValue>) => {
      const fields: FieldArrayFieldsProps<IFieldValue> = fieldArrayProps.fields;
      return (
        <div>
          {fields.map((element: string, index) => {
            // CAUTION: the "map" call returns a string, since "map" is not our standard "map" but provided by redux-form
            // Therefore we need to retrieve the parent type explicitly via redux-form "get":
            const attribute: Attribute = fields.get(index).attribute;

            if (!isConfigurableParentAttribute(attribute)) {
              return null;
            }

            return (
              <ParentAttributeConfigurationFields
                fieldName={element}
                key={`${element}-${index}`}
                attribute={attribute}
                templateAttributes={filterTemplateSuggestions(props.templateAttributes, attribute)}
              />
            );
          })}
        </div>
      );
    },
    [props.templateAttributes],
  );

  const footerMessage = useMemo(() => {
    if (props.error) {
      return { message: props.error, intent: CalloutIntent.Danger };
    }
    if (props.usedInOtherSampleTypes) {
      return { message: selMessage({ id: "admin.sample.parent.template.warnMessage" }), intent: CalloutIntent.Warning };
    }
    return undefined;
  }, [props.error, props.usedInOtherSampleTypes]);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title={`Configure ${props.parentTypeName} Attributes`}
        mainButton={{
          label: "Apply",
          isDisabled: props.submitting || props.invalid,
          onPress: props.handleSubmit(handleSubmitData),
        }}
        errorCallout={
          footerMessage && {
            message: footerMessage.message,
            intent: footerMessage.intent,
          }
        }
        closeFn={state.close}
      >
        <p>You can add some specific attribute configuration when used in the context of this sample type.</p>

        <FieldArray
          name={CONFIGURE_PARENT_TYPE_ATTRIBUTE_ATTRIBUTES_FIELD_ARRAY}
          component={buildParentAttributeConfigurationFieldsComponent}
        />
      </DialogComponent>
    </Modal>
  );
};

const validate = (allValues: IInitialValues) => {
  const arrayValues = allValues[CONFIGURE_PARENT_TYPE_ATTRIBUTE_ATTRIBUTES_FIELD_ARRAY];
  const bothTextAndTemplateFilled =
    arrayValues &&
    !!arrayValues.some((rowValues) => {
      const defaultText = rowValues[CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT];
      const template = rowValues[CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE];
      return !!defaultText && !!template;
    });

  return bothTextAndTemplateFilled ? { _error: "You can fill only one of default text or template fields." } : {};
};

const ConfigureParentTypeAttributeDialogForm = reduxForm<IInitialValues, IConfigureParentTypeAttributeDialogOwnProps>({
  form: CONFIGURE_PARENT_TYPE_ATTRIBUTE_FORM_NAME,
  validate,
})(ConfigureParentTypeAttributeDialogImpl);

const ConfigureParentTypeAttributeDialog = (props: IConfigureParentTypeAttributeDialogOwnProps): JSX.Element => {
  const initialValues = useMemo<IFieldValue[]>(() => {
    return props.attributes.filter(isConfigurableParentAttribute).map((attribute: IAttributeWithDefault) => {
      return {
        attribute: attribute,
        [CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_NUMBER]:
          attribute.attributeDefault &&
          isDefinedNumber(attribute.attributeDefault.defaultNumber) &&
          String(attribute.attributeDefault.defaultNumber),
        [CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_TEXT]: attribute.attributeDefault?.defaultText,
        [CONFIGURE_PARENT_TYPE_ATTRIBUTE_DEFAULT_VOC_TERM]: attribute.attributeDefault?.defaultTerm,
        [CONFIGURE_PARENT_TYPE_ATTRIBUTE_TEMPLATE]: attribute.attributeDefault?.template,
      };
    });
  }, [props.attributes]);

  return <ConfigureParentTypeAttributeDialogForm {...props} initialValues={{ attributes: initialValues }} />;
};

export { ConfigureParentTypeAttributeDialog, ConfigureParentTypeAttributeDialogImpl };
export default ConfigureParentTypeAttributeDialog;
